import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, Stack, TextField, Button, Divider, IconButton, MenuItem, Chip } from '@mui/material';
import { Close, FilterList, RestartAlt } from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * Reusable Filter Drawer Component
 *
 * @param {boolean} open - Whether the drawer is open
 * @param {function} onClose - Function to close the drawer
 * @param {function} onApply - Function called with the filter object when "Apply Filters" is clicked
 * @param {object} initialFilters - Initial state of filters
 * @param {Array} config - Array of field configurations
 *
 * Config Item Structure:
 * {
 *   name: 'status', // Key for the filter
 *   label: 'Status', // Label for the input
 *   type: 'select' | 'text' | 'date' | 'number',
 *   options: [{ label: 'Active', value: 'active' }] // For select type
 * }
 */
export default function FilterDrawer({ open, onClose, onApply, initialFilters = {}, config = [] }) {
    const [filters, setFilters] = useState(initialFilters);

    // Update local state when initialFilters change (e.g. from parent reset)
    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters, open]);

    const handleChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleReset = () => {
        // Reset to empty strings or defaults based on config
        const resetState = config.reduce((acc, field) => {
            acc[field.name] = '';
            return acc;
        }, {});
        setFilters(resetState);
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    const countActiveFilters = () => {
        return Object.values(filters).filter((val) => val !== '' && val !== null && val !== undefined).length;
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box
                sx={{
                    width: 380,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterList color="primary" />
                        <Typography variant="h6" fontWeight={700}>
                            Filters
                        </Typography>
                        {countActiveFilters() > 0 && (
                            <Chip label={countActiveFilters()} size="small" color="primary" sx={{ height: 20, minWidth: 20 }} />
                        )}
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {/* Filters Content */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
                    <Stack spacing={3}>
                        {config.map((field) => (
                            <Box key={field.name}>
                                {field.type === 'select' && (
                                    <TextField
                                        select
                                        fullWidth
                                        label={field.label}
                                        value={filters[field.name] || ''}
                                        onChange={(e) => handleChange(field.name, e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value="">
                                            <em>Any</em>
                                        </MenuItem>
                                        {field.options?.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}

                                {field.type === 'text' && (
                                    <TextField
                                        fullWidth
                                        label={field.label}
                                        value={filters[field.name] || ''}
                                        onChange={(e) => handleChange(field.name, e.target.value)}
                                        size="small"
                                        placeholder={field.placeholder || ''}
                                    />
                                )}

                                {field.type === 'number' && (
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={field.label}
                                        value={filters[field.name] || ''}
                                        onChange={(e) => handleChange(field.name, e.target.value)}
                                        size="small"
                                    />
                                )}

                                {field.type === 'date' && (
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label={field.label}
                                        InputLabelProps={{ shrink: true }}
                                        value={filters[field.name] || ''}
                                        onChange={(e) => handleChange(field.name, e.target.value)}
                                        size="small"
                                    />
                                )}
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* Footer Actions */}
                <Box mt={3} pt={2} borderTop="1px solid #eee">
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            fullWidth
                            startIcon={<RestartAlt />}
                            onClick={handleReset}
                            disabled={countActiveFilters() === 0}
                        >
                            Reset
                        </Button>
                        <Button variant="contained" color="primary" fullWidth onClick={handleApply} disableElevation>
                            Apply Filters
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
}

FilterDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
    config: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(['text', 'select', 'date', 'number']).isRequired,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    value: PropTypes.any.isRequired
                })
            )
        })
    ).isRequired
};
